import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { agentesApi, type Agente } from '@/lib/api/oto';
import { useAuthStore } from '@/lib/stores/authStore';
import { useBairros } from '@/lib/hooks/useBairros';
import { toast } from '@/lib/stores/toastStore';
import { Drawer } from '@/design-system/Drawer';
import { Info } from '@/design-system/Info';

export function AgentesPage() {
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token) || '';
  const queryClient = useQueryClient();
  const { bairros } = useBairros();

  const [modal, setModal] = useState<Agente | 'novo' | null>(null);

  const erroDe = (err: any, padrao: string) => {
    const msg = err?.response?.data?.message;
    return Array.isArray(msg) ? msg.join(' · ') : msg || padrao;
  };

  const { data: agentes = [], isLoading } = useQuery({
    queryKey: ['agentes'],
    queryFn: () => agentesApi.list(token),
    enabled: !!token,
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: ['agentes'] });

  const criar = useMutation({
    mutationFn: (dados: Partial<Agente>) => agentesApi.create(dados, token),
    onSuccess: () => {
      invalidar();
      // "Integrado ao OTO" seria falso: nenhum serviço lê `oto.agente`.
      toast('Agente registrado no catálogo do mandato.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao criar o agente.')),
  });

  const atualizar = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: Partial<Agente> }) =>
      agentesApi.update(id, dados, token),
    onSuccess: () => {
      invalidar();
      toast('Agente atualizado.');
      setModal(null);
    },
    // O servidor recusa redefinir agentes base do produto
    onError: (err: any) => toast(erroDe(err, 'Erro ao atualizar o agente.')),
  });

  const excluir = useMutation({
    mutationFn: (id: string) => agentesApi.delete(id, token),
    onSuccess: () => {
      invalidar();
      toast('Agente removido.');
      setModal(null);
    },
    onError: (err: any) => toast(erroDe(err, 'Erro ao remover o agente.')),
  });

  const base = agentes.filter((a) => a.origem === 'sistema');
  const custom = agentes.filter((a) => a.origem === 'custom');

  const alternarAtivo = (a: Agente) =>
    atualizar.mutate({ id: a.id, dados: { ativo: !a.ativo } });

  const cartao = (a: Agente) => (
    <div
      key={a.id}
      className="card"
      style={{ borderLeft: `4px solid ${a.ativo ? '#B26A00' : 'var(--line)'}`, opacity: a.ativo ? 1 : 0.6 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: 15, color: '#B26A00' }}>{a.nome}</h2>
        <button
          className={`tog ${a.ativo ? 'on' : ''}`}
          style={{ cursor: 'pointer' }}
          disabled={atualizar.isPending}
          onClick={() => alternarAtivo(a)}
        >
          {a.ativo ? 'Ativo' : 'Inativo'}
        </button>
      </div>

      {a.especialidade && (
        <span className="chip neutral" style={{ fontSize: 11, marginTop: 6, display: 'inline-block' }}>
          {a.especialidade}
        </span>
      )}
      {a.bairro && (
        <span className="chip eleg" style={{ fontSize: 11, marginTop: 6, marginLeft: 4, display: 'inline-block' }}>
          {a.bairro}
        </span>
      )}

      <p style={{ fontSize: 13, color: 'var(--ink2)', marginTop: 8, lineHeight: 1.5 }}>{a.papel}</p>

      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <button className="btn sm" onClick={() => navigate('/agente')}>
          Conversar
        </button>
        {a.origem === 'custom' ? (
          <>
            <button className="btn sm" onClick={() => setModal(a)}>
              ✏️ Editar
            </button>
            <button
              className="btn sm"
              style={{ color: 'var(--crit)', marginLeft: 'auto' }}
              disabled={excluir.isPending}
              onClick={() => {
                if (window.confirm(`Remover o agente "${a.nome}"?`)) excluir.mutate(a.id);
              }}
            >
              Excluir
            </button>
          </>
        ) : (
          <span
            className="chip neutral"
            style={{ fontSize: 10, marginLeft: 'auto', alignSelf: 'center' }}
            title="Agentes base acompanham o produto e não podem ser redefinidos"
          >
            agente base
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="page" id="p-agentes">
      <div className="head">
        <div>
          <h1 style={{ color: '#B26A00' }}>Meus agentes</h1>
          <p>
            Catálogo dos estrategistas do mandato: os cinco agentes base do produto e os que você
            criar, dedicados a um território ou a um tema. Nome, papel e especialidade ficam
            registrados aqui.
          </p>
        </div>
        <div className="act">
          <button className="btn pri" onClick={() => setModal('novo')}>
            + Novo agente
          </button>
        </div>
      </div>

      {/*
        NOTA DE MÉTODO, no lugar da do protótipo.

        proto:1597 traz "Regras de todos os agentes": só dados agregados e
        autorizados, fonte e período em toda resposta, declaram quando não há
        dado, nunca inferem opinião política, nunca alteram dados oficiais.
        Parte disso é verdade — mas do Agente analista, que é determinístico,
        e não destes registros. Enunciar as regras AQUI daria a entender que
        estes cadastros governam alguma geração, e eles não governam nenhuma:
        é o oposto do que a nota se propõe a fazer. A lacuna é a informação
        honesta.
      */}
      <div className="note warn" data-testid="nota-agentes-sem-efeito" style={{ marginBottom: 12 }}>
        <b>Este cadastro não muda nenhuma geração.</b> Nada no servidor lê{' '}
        <span className="mono">oto.agente</span>. O que governa o tom das estratégias é a persona,
        em <b>Perfil &amp; OTO</b>.
        <Info titulo="Onde o agente escolhido aparece, se não na geração">
          O gerador de estratégia monta o contexto a partir da persona e do mandato. No Agente
          analista, o agente escolhido só carimba um nome na resposta: o texto sai igual com
          qualquer um.
        </Info>
      </div>

      {isLoading && <div className="empty">Carregando agentes...</div>}

      {!isLoading && (
        <>
          <h2 style={{ fontSize: 14, margin: '4px 0 8px' }}>
            Estrategistas base <span style={{ color: 'var(--ink3)', fontWeight: 400 }}>({base.length})</span>
          </h2>
          <div className="grid g3">{base.map(cartao)}</div>

          <h2 style={{ fontSize: 14, margin: '20px 0 8px' }}>
            Agentes do mandato <span style={{ color: 'var(--ink3)', fontWeight: 400 }}>({custom.length})</span>
          </h2>
          {custom.length === 0 ? (
            <div className="empty">
              Nenhum agente próprio ainda. Use <b>+ Novo agente</b> para criar um dedicado a um
              território ou pauta.
            </div>
          ) : (
            <div className="grid g3">{custom.map(cartao)}</div>
          )}
        </>
      )}

      {modal && (
        <ModalAgente
          agente={modal === 'novo' ? null : modal}
          bairros={bairros.map((b) => b.n)}
          salvando={criar.isPending || atualizar.isPending}
          onCancelar={() => setModal(null)}
          onSalvar={(dados) => {
            if (modal === 'novo') criar.mutate(dados);
            else atualizar.mutate({ id: modal.id, dados });
          }}
        />
      )}
    </div>
  );
}

function ModalAgente({
  agente,
  bairros,
  salvando,
  onCancelar,
  onSalvar,
}: {
  agente: Agente | null;
  bairros: string[];
  salvando: boolean;
  onCancelar: () => void;
  onSalvar: (dados: Partial<Agente>) => void;
}) {
  const [nome, setNome] = useState(agente?.nome || '');
  const [papel, setPapel] = useState(agente?.papel || '');
  const [especialidade, setEspecialidade] = useState(agente?.especialidade || '');
  const [bairro, setBairro] = useState(agente?.bairro || '');

  const submeter = (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim() || !papel.trim()) {
      toast('Nome e papel do agente são obrigatórios.');
      return;
    }
    onSalvar({
      nome: nome.trim(),
      papel: papel.trim(),
      especialidade: especialidade.trim() || undefined,
      bairro: bairro || undefined,
    });
  };

  return (
    <form onSubmit={submeter}>
      <Drawer
        aberto
        titulo={agente ? 'Editar agente' : 'Novo agente do mandato'}
        aoFechar={onCancelar}
        rodape={
          <>
            <button type="button" className="btn" onClick={onCancelar}>
              Cancelar
            </button>
            <button type="submit" className="btn pri" disabled={salvando}>
              {salvando ? 'Salvando...' : agente ? 'Salvar alterações' : 'Criar agente'}
            </button>
          </>
        }
      >
        <div className="field">
          <label htmlFor="ag-nome">Nome do agente *</label>
          <input
            id="ag-nome"
            className="in"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex: Estrategista de Messejana"
            required
          />
        </div>

        <div className="field">
          <label htmlFor="ag-papel">Papel *</label>
          <textarea
            id="ag-papel"
            className="in"
            style={{ minHeight: 90 }}
            value={papel}
            onChange={(e) => setPapel(e.target.value)}
            placeholder="O que este agente observa e recomenda"
            required
          />
        </div>

        <div className="grid g2">
          <div className="field">
            <label htmlFor="ag-especialidade">Especialidade</label>
            <input
              id="ag-especialidade"
              className="in"
              value={especialidade}
              onChange={(e) => setEspecialidade(e.target.value)}
              placeholder="Ex: Monitoramento territorial"
            />
          </div>
          <div className="field">
            <label htmlFor="ag-bairro">Território dedicado</label>
            <select
              id="ag-bairro"
              className="in"
              value={bairro}
              onChange={(e) => setBairro(e.target.value)}
            >
              <option value="">Todo o mandato</option>
              {bairros.map((b) => (
                <option key={b} value={b}>
                  {b}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/*
          Os dois campos que o protótipo pede aqui (proto:1517) — "Instruções
          (como ele deve pensar)" e sete toggles de "Fontes que pode usar" —
          ficam declarados como lacuna, não desenhados.

          Desenhá-los seria pior que a ausência em dois níveis: o texto não teria
          coluna onde cair, e mesmo que tivesse não mudaria nada, porque nada no
          servidor lê `oto.agente`.
        */}
        <div className="note warn" style={{ fontSize: 12 }}>
          <b>O que este cadastro não faz.</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            <li>
              <b>Não há campo de instruções.</b>{' '}
              <span className="mono">oto.agente</span> não tem coluna para isso.
              <Info titulo="Por que esse campo não teria onde cair">
                A tabela guarda nome, papel, especialidade, território, ativo e ordem, e{' '}
                <span className="mono">CreateAgenteDto</span> declara exatamente esses campos. Com{' '}
                <span className="mono">ValidationPipe(&#123; whitelist: true &#125;)</span>, um
                texto a mais seria descartado sem aviso.
              </Info>
            </li>
            <li>
              <b>Não há seleção de fontes autorizadas.</b> O gerador de estratégias (
              <span className="mono">oto/services/estrategias.service.ts</span>) monta o pedido ao
              modelo com a persona e o mandato; agente não entra.
              <Info titulo="Por que ligar fontes aqui não mudaria nada">
                Também não existe coluna para essa escolha e, acima disso, nenhum serviço lê o
                registro do agente. Sete chaves ligadas e desligadas aqui não mudariam uma palavra
                do que o OTO responde.
              </Info>
            </li>
          </ul>
        </div>

      </Drawer>
    </form>
  );
}
